import InventoryItem from 'src/models/inventory-item/InventoryItem'
import InventoryItemUnit from 'src/models/inventory-item/InventoryItemUnit'
import Location from 'src/models/location/Location'
import Model, { encoders, ID, IsoDateTime, JsonObject } from 'src/models/model'
import RegisterStation from 'src/models/register/RegisterStation'

export const ADJUSTMENT_TYPE_OPTIONS = {
  DAMAGE: 'Damage',
  MISC: 'Miscellaneous',
  PREPARATION: 'Preparation',
  RECEIPT: 'Receipt',
  STOCKTAKE: 'Stocktake',
  THEFT: 'Theft',
  TRANSFER: 'Transfer',
  TRANSFER_RECEIPT: 'Transfer Receipt',
  WASTE: 'Waste'
}

export const ADJUSTMENT_TYPES_PLURAL = {
  [ADJUSTMENT_TYPE_OPTIONS.DAMAGE]: 'Damage',
  [ADJUSTMENT_TYPE_OPTIONS.MISC]: 'Miscellaneous',
  [ADJUSTMENT_TYPE_OPTIONS.PREPARATION]: 'Preparations',
  [ADJUSTMENT_TYPE_OPTIONS.RECEIPT]: 'Receipts',
  [ADJUSTMENT_TYPE_OPTIONS.STOCKTAKE]: 'Stocktakes',
  [ADJUSTMENT_TYPE_OPTIONS.THEFT]: 'Theft',
  [ADJUSTMENT_TYPE_OPTIONS.TRANSFER]: 'Transfers',
  [ADJUSTMENT_TYPE_OPTIONS.TRANSFER_RECEIPT]: 'Transfer Receipts',
  [ADJUSTMENT_TYPE_OPTIONS.WASTE]: 'Wastage'
}

ADJUSTMENT_TYPE_OPTIONS.ALL = _.values(ADJUSTMENT_TYPE_OPTIONS)

ADJUSTMENT_TYPE_OPTIONS.BULK = [
  ADJUSTMENT_TYPE_OPTIONS.DAMAGE,
  ADJUSTMENT_TYPE_OPTIONS.MISC,
  ADJUSTMENT_TYPE_OPTIONS.PREPARATION,
  ADJUSTMENT_TYPE_OPTIONS.RECEIPT,
  ADJUSTMENT_TYPE_OPTIONS.STOCKTAKE,
  ADJUSTMENT_TYPE_OPTIONS.THEFT,
  ADJUSTMENT_TYPE_OPTIONS.TRANSFER,
  ADJUSTMENT_TYPE_OPTIONS.WASTE
]

ADJUSTMENT_TYPE_OPTIONS.PREP = [
  ADJUSTMENT_TYPE_OPTIONS.DAMAGE,
  ADJUSTMENT_TYPE_OPTIONS.MISC,
  ADJUSTMENT_TYPE_OPTIONS.PREPARATION,
  ADJUSTMENT_TYPE_OPTIONS.STOCKTAKE,
  ADJUSTMENT_TYPE_OPTIONS.THEFT,
  ADJUSTMENT_TYPE_OPTIONS.TRANSFER,
  ADJUSTMENT_TYPE_OPTIONS.WASTE
]

ADJUSTMENT_TYPE_OPTIONS.NO_PREP = [
  ADJUSTMENT_TYPE_OPTIONS.DAMAGE,
  ADJUSTMENT_TYPE_OPTIONS.MISC,
  ADJUSTMENT_TYPE_OPTIONS.RECEIPT,
  ADJUSTMENT_TYPE_OPTIONS.STOCKTAKE,
  ADJUSTMENT_TYPE_OPTIONS.THEFT,
  ADJUSTMENT_TYPE_OPTIONS.TRANSFER,
  ADJUSTMENT_TYPE_OPTIONS.WASTE
]

export default class InventoryItemAdjustment extends Model {
  static name = 'InventoryItemAdjustment'

  static versioned = true

  static deletable = true

  static employeeRequiredPermissionCategories = [Model.permissionCategories.Inventory]

  static define () {
    this.fields = {
      id: {
        type: ID
      },
      adjustmentType: {
        type: String
      },
      adjustmentReason: {
        type: String
      },
      location: {
        type: Location,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      registerStation: {
        type: RegisterStation,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      transferLocationId: {
        type: ID,
        default: null
      },
      transferRegisterStationId: {
        type: ID,
        default: null
      },
      inventoryItem: {
        type: InventoryItem,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      inventoryItemUnit: {
        type: InventoryItemUnit,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      unitQuantity: {
        type: Number,
        default: 0
      },
      unitQuantityConfig: {
        type: JsonObject,
        default: () => ({})
      },
      baseQuantity: {
        type: Number,
        output: true
      },
      cost: {
        type: Number,
        encode: encoders.cents,
        default: null
      },
      committedAt: {
        type: IsoDateTime,
        default: null,
        output: true,
        sortable: true
      },
      createdAt: {
        type: IsoDateTime,
        output: true,
        sortable: true
      },
      action: {
        type: String,
        default: null,
        input: true
      },
      locationOrRegisterStation: {
        type: Object,
        default: null,
        local: true
      },
      transferLocationOrRegisterStation: {
        type: Object,
        default: null,
        local: true
      }
    }

    this.computed = {
      adjustmentTypeOptions () {
        if (!this.inventoryItem) {
          return []
        } else if (this.inventoryItem.isPreparation) {
          return ADJUSTMENT_TYPE_OPTIONS.PREP
        } else {
          return ADJUSTMENT_TYPE_OPTIONS.NO_PREP
        }
      },
      showAdjustmentReason () {
        return (
          this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.DAMAGE ||
          this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.MISC ||
          this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.THEFT ||
          this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.TRANSFER ||
          this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.WASTE
        )
      },
      isMisc () {
        return this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.MISC
      },
      isPreparation () {
        return this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.PREPARATION
      },
      isReceipt () {
        return this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.RECEIPT
      },
      isStocktake () {
        return this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.STOCKTAKE
      },
      isWaste () {
        return this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.WASTE
      },
      isTransfer () {
        return this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.TRANSFER
      },
      isTransferReceipt () {
        return this.adjustmentType === ADJUSTMENT_TYPE_OPTIONS.TRANSFER_RECEIPT
      },
      isCommitted () {
        return !!this.committedAt
      },
      hasLocationOrRegisterStation () {
        return !!(
          this.location || this.registerStation
        )
      },
      locationOrRegisterStationDisplayValue () {
        return (
          this.location ||
          this.registerStation
        )?.inventoryStrategyEntityDisplayName
      },
      hasTransferLocationOrRegisterStation () {
        return !!(
          this.transferLocationId || this.transferRegisterStationId
        )
      },
      transferLocationOrRegisterStationDisplayValue () {
        return (
          _.find(this.configuration.currentCircuit.locations.data, ({ id }) => id === this.transferLocationId) ||
          _.find(this.configuration.registerStations.data, ({ id }) => id === this.transferRegisterStationId)
        )?.inventoryStrategyEntityDisplayName
      },
      computedUnitQuantity () {
        if (this.inventoryItem) {
          return _(this.unitQuantityConfig).entries().map(([unitId, qty]) => {
            const inventoryItemUnit = this.inventoryItem.inventoryItemUnits.data.find(unit => unit.id === Number(unitId))
            return inventoryItemUnit ? qty * inventoryItemUnit.baseQuantity : 0
          }).sum()
        } else {
          return 0
        }
      },
      adjustmentReasonRules () {
        if (this.isMisc) {
          return [
            val => (val !== '' && val !== null && val !== undefined) || 'Field is required',
            val => !val || val.length < 256 || 'Max length 255 characters'
          ]
        } else {
          return [val => !val || val.length < 256 || 'Max length 255 characters']
        }
      },
      unitQuantityRules () {
        if (this.isMisc) {
          return [val => (val !== '' && val !== null && val !== undefined && val !== 0) || 'Field is required']
        } else if (this.isStocktake) {
          return [val => (val !== '' && val !== null && val !== undefined && val >= 0) || 'Field is required']
        } else {
          return [val => (val !== '' && val !== null && val !== undefined && val > 0) || 'Field is required']
        }
      },
      unitQuantityRulesForCalculator () {
        if (this.isMisc) {
          return [val => (val !== '' && val !== null && val !== undefined) || 'Field is required']
        } else {
          return [val => (val !== '' && val !== null && val !== undefined && val >= 0) || 'Field is required']
        }
      },
      readonly () {
        return !_.isNil(this.committedAt) || this.isTransferReceipt
      },
      deletable () {
        return !this.readonly
      }
    }

    this.watchers = [
      [
        function () { return this.inventoryItem },
        function () {
          this.inventoryItemUnit = this.inventoryItem?.baseInventoryItemUnit || null
        }
      ],
      [
        function () { return [this.inventoryItem, this.inventoryItemUnit, this.unitQuantity] },
        function () {
          this.setUnitQuantityConfig()
        }
      ],
      [
        function () { return this.computedUnitQuantity },
        function () {
          if (this.unitQuantity === this.computedUnitQuantity) return

          this.unitQuantity = this.computedUnitQuantity
        }
      ],
      [
        function () { return this.locationOrRegisterStation },
        function () {
          if (this.locationOrRegisterStation?.typeName === 'Location') {
            this.location = this.locationOrRegisterStation
            this.registerStation = null
          } else if (this.locationOrRegisterStation?.typeName === 'RegisterStation') {
            this.registerStation = this.locationOrRegisterStation
            this.location = null
          } else {
            this.location = null
            this.registerStation = null
          }
        }
      ],
      [
        function () { return this.transferLocationOrRegisterStation },
        function () {
          if (this.transferLocationOrRegisterStation?.typeName === 'Location') {
            this.transferLocationId = this.transferLocationOrRegisterStation.id
            this.transferRegisterStationId = null
          } else if (this.transferLocationOrRegisterStation?.typeName === 'RegisterStation') {
            this.transferRegisterStationId = this.transferLocationOrRegisterStation.id
            this.transferLocationId = null
          } else {
            this.transferLocationId = null
            this.transferRegisterStationId = null
          }
        }
      ]
    ]

    this.paginate = true
    this.filterable = true
  }

  setUnitQuantityConfig () {
    if (
      this.inventoryItem &&
      this.inventoryItemUnit &&
      this.inventoryItemUnit.id === this.inventoryItem.baseInventoryItemUnit.id
    ) {
      if (this.unitQuantity !== this.computedUnitQuantity) {
        this.unitQuantityConfig = _.reduce(this.inventoryItem.inventoryItemUnits.data, (acc, unit) => {
          if (unit.id === this.inventoryItem.baseInventoryItemUnit.id) {
            acc[unit.id] = this.unitQuantity || 0
          } else {
            acc[unit.id] = 0
          }

          return acc
        }, {})
      }
    } else if (!_.isEmpty(this.unitQuantityConfig)) {
      this.unitQuantityConfig = {}
    }
  }
}
